import { createTheme } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-license";

// MUI X Pro license key — required for grid grouping, tree data, and
// Pro components. The 30-day trial expired during development; you need
// a real key now or the grids show a "MUI X Missing license key"
// watermark and Pro features are read-only.
//
// To set it (any one of these works, in priority order):
//   1. Shell env BEFORE running `make new-instance` / `docker compose build`:
//        export VITE_MUI_LICENSE_KEY="<paste from mui.com order page>"
//        make new-instance ORG="Acme"
//   2. Add to repo-root `.env` (also picked up by docker compose):
//        VITE_MUI_LICENSE_KEY=<key>
//   3. For local-only dev outside docker, drop into frontend/.env.local
//      (gitignored) and run `npm run dev` — Vite picks it up automatically.
const licenseKey = import.meta.env.VITE_MUI_LICENSE_KEY as string | undefined;
if (licenseKey && licenseKey.length > 10) {
  LicenseInfo.setLicenseKey(licenseKey);
} else if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.warn(
    "[mui-x] No VITE_MUI_LICENSE_KEY set at build time — grids will show a " +
    "watermark and Pro features (grouping, tree data) won't work. Set it in " +
    "the repo-root .env or pass via docker compose --build-arg.",
  );
}

const sharedTypography = {
  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: 13,
} as const;

const sharedShape = { borderRadius: 6 } as const;

const sharedComponents = {
  MuiButton: { styleOverrides: { root: { textTransform: "none" as const, fontWeight: 500 } } },
  MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
} as const;

// ── Bynaus colour tokens ─────────────────────────────────────────────────────
// Keep in sync with bynaus-edge/templates/index.html
const BYNAUS = {
  // Backgrounds
  bgDeep:   "#080c14",  // page / shell background
  bgCard:   "#0f1520",  // paper / cards / dialogs
  bgBorder: "#1a2235",  // dividers, table cell borders
  bgInput:  "#111827",  // input fills

  // Accent (amber)
  amber:    "#F59E0B",
  amberDark: "#D97706",

  // Text
  textPrimary:   "#e2e8f0",
  textSecondary: "#64748b",

  // Status
  red:      "#f87171",
  green:    "#34d399",
} as const;

export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: BYNAUS.amber, dark: BYNAUS.amberDark, contrastText: "#0f172a" },
    secondary: { main: "#64748b" },
    error: { main: "#dc2626" },
    success: { main: "#059669" },
    warning: { main: BYNAUS.amberDark },
    info: { main: "#0ea5e9" },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#475569",
    },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: sharedComponents,
});

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: BYNAUS.amber, dark: BYNAUS.amberDark, contrastText: "#0c0f17" },
    secondary: { main: "#94a3b8" },
    error: { main: BYNAUS.red },
    success: { main: BYNAUS.green },
    warning: { main: BYNAUS.amber },
    info: { main: "#38bdf8" },
    background: {
      default: BYNAUS.bgDeep,   // #080c14 — Bynaus signature dark navy
      paper:   BYNAUS.bgCard,   // #0f1520 — cards, dialogs, drawers
    },
    text: {
      primary:   BYNAUS.textPrimary,   // #e2e8f0
      secondary: BYNAUS.textSecondary, // #64748b
    },
    divider: BYNAUS.bgBorder,          // #1a2235
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    ...sharedComponents,
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none" as const, fontWeight: 500 },
        // Contained primary uses amber bg with dark text — keep readable
        containedPrimary: {
          color: "#0c0f17",
          "&:hover": { backgroundColor: BYNAUS.amberDark },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",  // remove MUI dark-mode gradient
          borderColor: BYNAUS.bgBorder,
        },
        outlined: {
          border: `1px solid ${BYNAUS.bgBorder}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: BYNAUS.bgBorder },
        head: { color: BYNAUS.textSecondary, fontWeight: 600, fontSize: 11 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: "rgba(245,158,11,0.04)" },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: BYNAUS.bgBorder },
            "&:hover fieldset": { borderColor: BYNAUS.amber },
            "&.Mui-focused fieldset": { borderColor: BYNAUS.amber },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": { color: BYNAUS.amber },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: BYNAUS.amber },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
        colorPrimary: {
          backgroundColor: "rgba(245,158,11,0.15)",
          color: BYNAUS.amber,
          border: `1px solid rgba(245,158,11,0.3)`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: "none",
          border: `1px solid ${BYNAUS.bgBorder}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${BYNAUS.bgBorder}`, paddingBottom: 12 },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { borderTop: `1px solid ${BYNAUS.bgBorder}` },
      },
    },
  },
});

// Legacy export so any import of `theme` still compiles (it's the dark Bynaus theme).
export const theme = darkTheme;
